import React, { useEffect, useMemo, useState } from "react"
import { Card, CardBody, Row, Col, Table, Spinner, Button, Input, Nav, NavItem, NavLink } from "reactstrap"
import { useNavigate } from "react-router-dom"
import ReactApexChart from "react-apexcharts"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getFleetDashboard, getFleetAllotments, getFleetFuelReport, getFleetFuelExpenseSummary, getFleetTransportationReport, getFleetMonthlyExpensesReport, getFleetAverageCostingReport } from "../../services/fleet"
import { toastError } from "../../helpers/msg_helper"
import useCollapsedSidebar from "../../hooks/useCollapsedSidebar"

const money = v =>
  v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const num = v => (v == null ? "0" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }))
const pctColor = p => (p == null ? "#64748b" : p > 100 ? "#dc2626" : p >= 70 ? "#16a34a" : "#d97706")

const PALETTE = ["#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#10b981", "#ec4899"]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const Kpi = ({ label, value, sub, fg, tint }) => (
  <Col xs={6} sm={4} md>

    <div style={{ border: "1px solid #eef0f4", borderRadius: 12, padding: "14px 16px", background: tint || "#fff", height: "100%" }}>
      <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".03em" }}>{label}</div>
      <div className="fw-bold" style={{ fontSize: 22, color: fg || "#1f2937", lineHeight: 1.3 }}>{value}</div>
      {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  </Col>
)

/** Operations → Fleet → Dashboard. Fleet composition & asset overview from the vehicle master. */
const FleetDashboard = () => {
  useCollapsedSidebar()
  document.title = "Fleet Overview | Calyvern"
  const navigate = useNavigate()
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  // fuel-consumption pie (by vehicle type) for a chosen month — defaults to the current month
  const [fcMonth, setFcMonth] = useState(new Date().toISOString().slice(0, 7))
  const [fc, setFc] = useState({ rows: [], summary: {} })
  // fuel-expense tiles (this/last month & year) + a custom date-range filter
  const [exp, setExp] = useState({})
  const [expFrom, setExpFrom] = useState("")
  const [expTo, setExpTo] = useState("")
  // Transportation Analysis report
  const [transMonth, setTransMonth] = useState(new Date().toISOString().slice(0, 7))
  const [trans, setTrans] = useState({ rows: [], totals: {} })
  // Monthly Expenses Details (year grid, with a month/whole-year switch)
  const [mxYear, setMxYear] = useState(String(new Date().getFullYear()))
  const [mxMonth, setMxMonth] = useState("") // "" = whole year
  const [mx, setMx] = useState({ rows: [] })
  // Average Costing (expense heads × month) + manual Invoiced / Employees inputs (session-only)
  const [acYear, setAcYear] = useState(String(new Date().getFullYear()))
  const [ac, setAc] = useState({ rows: [], transport_cost: [], transport_cost_total: 0 })

  // inline reports
  const [reportTab, setReportTab] = useState("allotment")
  const [allotDate, setAllotDate] = useState(new Date().toISOString().slice(0, 10))
  const [fuelMonth, setFuelMonth] = useState("2026-07")
  const [allot, setAllot] = useState({ rows: [], summary: {} })
  const [fuel, setFuel] = useState({ rows: [], summary: {} })
  const [repLoading, setRepLoading] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await getFleetDashboard()
        setD(r?.data || null)
      } catch (e) { toastError(e?.message || "Failed to load fleet dashboard") } finally { setLoading(false) }
    })()
  }, [])

  useEffect(() => {
    if (reportTab !== "allotment") return
    ;(async () => {
      try { setRepLoading(true); const r = await getFleetAllotments({ date: allotDate }); setAllot(r?.data || { rows: [], summary: {} }) }
      catch (e) { /* best-effort */ } finally { setRepLoading(false) }
    })()
  }, [reportTab, allotDate])

  useEffect(() => {
    if (reportTab !== "fuel") return
    ;(async () => {
      try { setRepLoading(true); const r = await getFleetFuelReport({ month: fuelMonth }); setFuel(r?.data || { rows: [], summary: {} }) }
      catch (e) { /* best-effort */ } finally { setRepLoading(false) }
    })()
  }, [reportTab, fuelMonth])

  // load fuel consumption for the pie whenever the month changes
  useEffect(() => {
    ;(async () => {
      try { const r = await getFleetFuelReport({ month: fcMonth }); setFc(r?.data || { rows: [], summary: {} }) }
      catch (e) { /* best-effort */ }
    })()
  }, [fcMonth])

  // fuel-expense tiles + custom range total
  useEffect(() => {
    ;(async () => {
      try { const r = await getFleetFuelExpenseSummary({ from: expFrom, to: expTo }); setExp(r?.data || {}) }
      catch (e) { /* best-effort */ }
    })()
  }, [expFrom, expTo])

  // Transportation Analysis for the chosen month
  useEffect(() => {
    ;(async () => {
      try { const r = await getFleetTransportationReport({ month: transMonth }); setTrans(r?.data || { rows: [], totals: {} }) }
      catch (e) { /* best-effort */ }
    })()
  }, [transMonth])

  // Monthly Expenses Details for the chosen year
  useEffect(() => {
    ;(async () => {
      try { const r = await getFleetMonthlyExpensesReport({ year: mxYear }); setMx(r?.data || { rows: [] }) }
      catch (e) { /* best-effort */ }
    })()
  }, [mxYear])

  // Average Costing for the chosen year
  useEffect(() => {
    ;(async () => {
      try { const r = await getFleetAverageCostingReport({ year: acYear }); setAc(r?.data || { rows: [], transport_cost: [], transport_cost_total: 0 }) }
      catch (e) { /* best-effort */ }
    })()
  }, [acYear])

  const t = d?.totals || {}
  const byType = d?.by_type || []
  const byOwner = d?.by_owner || []
  const byCategory = d?.by_category || []

  // fuel consumption (AED) by vehicle type for the chosen month
  const fuelPie = useMemo(() => {
    const by = {}
    ;(fc.rows || []).forEach(r => { const k = r.vehicle_type || "Unspecified"; by[k] = (by[k] || 0) + Number(r.amount || 0) })
    const labels = Object.keys(by)
    const series = labels.map(k => Math.round(by[k] * 100) / 100)
    return {
      series, labels,
      options: {
        chart: { type: "pie" },
        labels, colors: PALETTE, legend: { position: "bottom" },
        dataLabels: { enabled: true, formatter: v => `${Number(v).toFixed(0)}%` },
        tooltip: { y: { formatter: v => `${money(v)} AED` } },
      },
    }
  }, [fc])

  const typeBar = useMemo(() => ({
    series: [{ name: "Vehicles", data: byType.map(x => x.count) }],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "50%", borderRadius: 4, distributed: true } },
      colors: PALETTE,
      xaxis: { categories: byType.map(x => x.label) },
      dataLabels: { enabled: true },
      legend: { show: false },
      grid: { borderColor: "#f1f1f1" },
    },
  }), [byType])

  const ownerBar = useMemo(() => ({
    series: [{ name: "Vehicles", data: byOwner.map(x => x.count) }],
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 4, distributed: true } },
      colors: PALETTE,
      xaxis: { categories: byOwner.map(x => x.owner) },
      legend: { show: false },
      dataLabels: { enabled: true },
      grid: { borderColor: "#f1f1f1" },
      // hover shows the per-vehicle-type breakup for that owner company
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const o = byOwner[dataPointIndex] || {}
          const rows = (o.types || []).map(ty =>
            `<div style="display:flex;justify-content:space-between;gap:16px"><span>${ty.type}</span><b>${ty.count}</b></div>`
          ).join("")
          return `<div style="padding:8px 10px;font-size:12px">
            <div style="font-weight:600;margin-bottom:4px">${o.owner || ""} · ${o.count ?? 0} vehicles</div>${rows}</div>`
        },
      },
    },
  }), [byOwner])

  // Monthly Expenses: a vehicle's cell for the chosen month (or its whole-year total)
  const mxCell = r => {
    const c = mxMonth === "" ? (r.year_total || {}) : (r.months?.[Number(mxMonth) - 1] || {})
    const total = mxMonth === ""
      ? Number(c.total || 0)
      : ["maintenance", "fuel", "parking_salik", "depreciation", "driver", "insurance"].reduce((s, k) => s + Number(c[k] || 0), 0)
    return { ...c, total }
  }
  const mxTotals = () => (mx.rows || []).reduce((a, r) => {
    const c = mxCell(r)
    ;["maintenance", "fuel", "parking_salik", "depreciation", "driver", "insurance", "total"].forEach(k => { a[k] = (a[k] || 0) + Number(c[k] || 0) })
    return a
  }, {})

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Breadcrumbs
            title="Fleet"
            breadcrumbObject={[
              { title: "Fleet", link: "/fleet-dashboard" },
              { title: "Dashboard", link: "/fleet-dashboard" },
            ]}
          />
          <div className="d-flex gap-2">
            <Button size="sm" color="primary" onClick={() => navigate("/fleet-allotment")}>
              <i className="bx bx-calendar-check me-1" /> Daily Allotment
            </Button>
            <Button size="sm" color="primary" outline onClick={() => navigate("/fleet-fuel")}>
              <i className="bx bx-gas-pump me-1" /> Fuel
            </Button>
            <Button size="sm" color="primary" outline onClick={() => navigate("/fleet-maintenance")}>
              <i className="bx bx-wrench me-1" /> Maintenance
            </Button>
            <Button size="sm" color="primary" outline onClick={() => navigate("/fleet")}>
              <i className="bx bx-list-ul me-1" /> Vehicles
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner /> </div>
        ) : (
          <>
            <Row className="g-2 mb-3">
              <Kpi label="Total Vehicles" value={num(t.total)} sub={`${num(t.active)} active`} fg="#1f2937" />
              <Kpi label="Active" value={num(t.active)} sub={`${num(t.inactive)} inactive`} fg="#16a34a" tint="#f0fdf4" />
              <Kpi label="Owned" value={num(t.own)} sub={`${num(t.rented)} rented`} fg="#7c3aed" tint="#faf5ff" />
              <Kpi label="Fleet Asset Value" value={money(t.asset_value)} sub="AED (opening balance)" fg="#0f766e" tint="#f0fdfa" />
              <Kpi label="Depreciation" value={money(t.depreciation)} sub="AED" fg="#b45309" tint="#fffbeb" />
            </Row>

            <Row className="g-3">
              <Col lg={12}>
                <Card className="mb-3"><CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <h6 className="fw-semibold mb-0">Vehicle Fuel Consumption</h6>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Input type="month" bsSize="sm" style={{ width: 150 }} value={fcMonth} onChange={e => setFcMonth(e.target.value)} />
                      <span className="text-muted small ms-2">Expense range</span>
                      <Input type="date" bsSize="sm" style={{ width: 140 }} value={expFrom} onChange={e => setExpFrom(e.target.value)} />
                      <span className="text-muted small">–</span>
                      <Input type="date" bsSize="sm" style={{ width: 140 }} value={expTo} onChange={e => setExpTo(e.target.value)} />
                    </div>
                  </div>
                  <Row className="g-3 align-items-center">
                    <Col lg={5}>
                      {fuelPie.series.length ? <ReactApexChart options={fuelPie.options} series={fuelPie.series} type="pie" height={280} />
                        : <div className="text-muted text-center py-5">No fuel entries for this month.</div>}
                    </Col>
                    <Col lg={7}>
                      <Row className="g-2">
                        {[
                          ["This Month", exp.this_month, "#0f766e", "#f0fdfa"],
                          ["Last Month", exp.last_month, "#4f46e5", "#eef2ff"],
                          ["This Year", exp.this_year, "#b45309", "#fffbeb"],
                          ["Last Year", exp.last_year, "#7c3aed", "#faf5ff"],
                        ].map(([lbl, val, fg, tint]) => (
                          <Col xs={6} key={lbl}>
                            <div style={{ border: "1px solid #eef0f4", borderRadius: 10, padding: "14px 16px", background: tint }}>
                              <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>{lbl}</div>
                              <div className="fw-bold" style={{ fontSize: 20, color: fg }}>{money(val)}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>AED</div>
                            </div>
                          </Col>
                        ))}
                        {expFrom && expTo && (
                          <Col xs={12}>
                            <div style={{ border: "1px dashed #cbd5e1", borderRadius: 10, padding: "12px 16px" }}>
                              <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>Selected range</div>
                              <div className="fw-bold" style={{ fontSize: 20, color: "#1f2937" }}>{money(exp.custom)} <span className="text-muted" style={{ fontSize: 12 }}>AED</span></div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </CardBody></Card>
              </Col>
            </Row>

            <Row className="g-3 mt-0">
              <Col lg={6}>
                <Card className="mb-3"><CardBody style={{ minHeight: 360 }}>
                  <h6 className="fw-semibold mb-1">By Vehicle Type</h6>
                  {byType.length ? <ReactApexChart options={typeBar.options} series={typeBar.series} type="bar" height={280} />
                    : <div className="text-muted text-center py-4">No vehicles.</div>}
                </CardBody></Card>
              </Col>
              <Col lg={6}>
                <Card className="mb-3"><CardBody style={{ minHeight: 360 }}>
                  <h6 className="fw-semibold mb-1">By Owner Company</h6>
                  {byOwner.length ? <ReactApexChart options={ownerBar.options} series={ownerBar.series} type="bar" height={280} />
                    : <div className="text-muted text-center py-4">—</div>}
                </CardBody></Card>
              </Col>
            </Row>

            <Row className="g-3 mt-0">
              <Col lg={12}>
                <Card className="mb-3"><CardBody>
                  <h6 className="fw-semibold mb-2">Asset Value by Owner</h6>
                  <div style={{ overflowX: "auto" }}>
                    <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>Owner</th>
                          <th className="text-end">Vehicles</th>
                          <th className="text-end">Asset (AED)</th>
                          <th className="text-end">Depreciation (AED)</th>
                          <th className="text-end">Accumulated Asset (AED)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byOwner.map((o, i) => (
                          <tr key={i}>
                            <td className="fw-500">{o.owner}</td>
                            <td className="text-end">{num(o.count)}</td>
                            <td className="text-end">{money(o.asset_value)}</td>
                            <td className="text-end">{money(o.depreciation)}</td>
                            <td className="text-end">{money(o.net_value)}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                          <td className="fw-600">Total</td>
                          <td className="text-end fw-600">{num(t.total)}</td>
                          <td className="text-end fw-600">{money(t.asset_value)}</td>
                          <td className="text-end fw-600">{money(t.depreciation)}</td>
                          <td className="text-end fw-600">{money((Number(t.asset_value) || 0) - (Number(t.depreciation) || 0))}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  {byCategory.length > 0 && (
                    <div className="mt-2 text-muted small">
                      Category:&nbsp;
                      {byCategory.map((c, i) => <span key={i} className="me-2">{c.label}: <b>{c.count}</b></span>)}
                    </div>
                  )}
                </CardBody></Card>
              </Col>
            </Row>

            {/* Transportation Analysis */}
            <Card className="mb-3"><CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2 gap-2">
                <h6 className="fw-semibold mb-0">Transportation Analysis</h6>
                <Input type="month" bsSize="sm" style={{ width: 160 }} value={transMonth} onChange={e => setTransMonth(e.target.value)} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>#</th><th>Vehicle</th><th>Category</th>
                      <th className="text-end">Maintenance</th><th className="text-end">Fuel</th>
                      <th className="text-end">Parking &amp; Salik</th>
                      <th className="text-end">Depreciation {num(trans.depreciation_pct)}% P/A</th>
                      <th className="text-end">Driver Salary &amp; Incentive</th><th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!trans.rows.length && <tr><td colSpan="9" className="text-center text-muted py-3">No data for this month.</td></tr>}
                    {trans.rows.map(r => (
                      <tr key={r.sl}>
                        <td className="text-muted">{r.sl}</td>
                        <td className="fw-500">{r.plate}</td>
                        <td>{r.category}</td>
                        <td className="text-end">{r.maintenance ? money(r.maintenance) : "—"}</td>
                        <td className="text-end">{r.fuel ? money(r.fuel) : "—"}</td>
                        <td className="text-end">{r.parking_salik ? money(r.parking_salik) : "—"}</td>
                        <td className="text-end">{r.depreciation ? money(r.depreciation) : "—"}</td>
                        <td className="text-end">{r.driver ? money(r.driver) : "—"}</td>
                        <td className="text-end fw-600">{money(r.total)}</td>
                      </tr>
                    ))}
                    {!!trans.rows.length && (
                      <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                        <td className="fw-600" colSpan="3">Total</td>
                        <td className="text-end fw-600">{money(trans.totals?.maintenance)}</td>
                        <td className="text-end fw-600">{money(trans.totals?.fuel)}</td>
                        <td className="text-end fw-600">{money(trans.totals?.parking_salik)}</td>
                        <td className="text-end fw-600">{money(trans.totals?.depreciation)}</td>
                        <td className="text-end fw-600">{money(trans.totals?.driver)}</td>
                        <td className="text-end fw-600">{money(trans.totals?.total)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody></Card>

            {/* Monthly Expenses Details */}
            <Card className="mb-3"><CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2 gap-2">
                <h6 className="fw-semibold mb-0">Monthly Expenses Details</h6>
                <div className="d-flex align-items-center gap-2">
                  <Input type="select" bsSize="sm" style={{ width: 110 }} value={mxYear} onChange={e => setMxYear(e.target.value)}>
                    {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                  </Input>
                  <Input type="select" bsSize="sm" style={{ width: 150 }} value={mxMonth} onChange={e => setMxMonth(e.target.value)}>
                    <option value="">Whole year</option>
                    {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((mn, i) => <option key={mn} value={i + 1}>{mn} {mxYear}</option>)}
                  </Input>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 45 }}>#</th><th>Vehicle</th><th>Year</th><th>Category</th><th>Driver</th><th>Company</th>
                      <th className="text-end">Value</th><th className="text-end">Maintenance</th><th className="text-end">Fuel</th>
                      <th className="text-end">Parking &amp; Salik</th><th className="text-end">Depreciation</th>
                      <th className="text-end">Driver Salary</th><th className="text-end">Insurance</th><th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!mx.rows?.length && <tr><td colSpan="14" className="text-center text-muted py-3">No data.</td></tr>}
                    {(mx.rows || []).map(r => {
                      const c = mxCell(r)
                      return (
                        <tr key={r.sl}>
                          <td className="text-muted">{r.sl}</td>
                          <td className="fw-500">{r.plate}</td>
                          <td>{r.model_year || "—"}</td>
                          <td>{r.category}</td>
                          <td>{r.driver}</td>
                          <td>{r.company}</td>
                          <td className="text-end">{money(r.value)}</td>
                          <td className="text-end">{c.maintenance ? money(c.maintenance) : "—"}</td>
                          <td className="text-end">{c.fuel ? money(c.fuel) : "—"}</td>
                          <td className="text-end">{c.parking_salik ? money(c.parking_salik) : "—"}</td>
                          <td className="text-end">{c.depreciation ? money(c.depreciation) : "—"}</td>
                          <td className="text-end">{c.driver ? money(c.driver) : "—"}</td>
                          <td className="text-end">{c.insurance ? money(c.insurance) : "—"}</td>
                          <td className="text-end fw-600">{money(c.total)}</td>
                        </tr>
                      )
                    })}
                    {!!mx.rows?.length && (() => { const tt = mxTotals(); return (
                      <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                        <td className="fw-600" colSpan="7">Total</td>
                        <td className="text-end fw-600">{money(tt.maintenance)}</td>
                        <td className="text-end fw-600">{money(tt.fuel)}</td>
                        <td className="text-end fw-600">{money(tt.parking_salik)}</td>
                        <td className="text-end fw-600">{money(tt.depreciation)}</td>
                        <td className="text-end fw-600">{money(tt.driver)}</td>
                        <td className="text-end fw-600">{money(tt.insurance)}</td>
                        <td className="text-end fw-600">{money(tt.total)}</td>
                      </tr>
                    ) })()}
                  </tbody>
                </Table>
              </div>
            </CardBody></Card>

            {/* Average Costing */}
            <Card className="mb-3"><CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2 gap-2">
                <h6 className="fw-semibold mb-0">Average Costing</h6>
                <Input type="select" bsSize="sm" style={{ width: 110 }} value={acYear} onChange={e => setAcYear(e.target.value)}>
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                </Input>
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table borderless className="align-middle tnt-table mb-0" style={{ fontSize: 12.5, minWidth: 1150 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 190 }}>Description</th>
                      {MONTHS.map(mn => <th key={mn} className="text-end">{mn}</th>)}
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ac.rows || []).map(r => (
                      <tr key={r.key}>
                        <td className="fw-500">{r.label}</td>
                        {r.months.map((v, m) => <td key={m} className="text-end">{v ? money(v) : "—"}</td>)}
                        <td className="text-end fw-600">{money(r.total)}</td>
                      </tr>
                    ))}
                    {/* Total Transportation Cost */}
                    <tr style={{ borderTop: "2px solid #e5e7eb", background: "#f8fafc" }}>
                      <td className="fw-600">Total Transportation Cost</td>
                      {(ac.transport_cost || []).map((v, m) => <td key={m} className="text-end fw-600">{money(v)}</td>)}
                      <td className="text-end fw-600">{money(ac.transport_cost_total)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </CardBody></Card>

            {/* Inline reports — Daily Allotment + Fuel Consumption */}
            <Card className="mb-3"><CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <h6 className="fw-semibold mb-0">Fleet Reports</h6>
                <div className="d-flex align-items-center gap-2">
                  {reportTab === "allotment" ? (
                    <>
                      <Input type="date" bsSize="sm" style={{ width: 150 }} value={allotDate} onChange={e => setAllotDate(e.target.value)} />
                      <Button size="sm" color="link" className="p-0" onClick={() => navigate("/fleet-allotment")}>Manage →</Button>
                    </>
                  ) : (
                    <>
                      <Input type="month" bsSize="sm" style={{ width: 150 }} value={fuelMonth} onChange={e => setFuelMonth(e.target.value)} />
                      <Button size="sm" color="link" className="p-0" onClick={() => navigate("/fleet-fuel")}>Manage →</Button>
                    </>
                  )}
                </div>
              </div>

              <Nav tabs className="mb-3">
                <NavItem><NavLink role="button" active={reportTab === "allotment"} onClick={() => setReportTab("allotment")}>Daily Allotment</NavLink></NavItem>
                <NavItem><NavLink role="button" active={reportTab === "fuel"} onClick={() => setReportTab("fuel")}>Fuel Consumption</NavLink></NavItem>
              </Nav>

              {repLoading ? <div className="text-center py-4"><Spinner size="sm" /></div> : reportTab === "allotment" ? (
                <>
                  <div className="text-muted small mb-2">
                    {allot.summary?.allotted || 0} vehicles allotted · carried {num(allot.summary?.carried)} / {num(allot.summary?.capacity)} ·
                    avg utilisation <b>{allot.summary?.avg_utilisation != null ? `${allot.summary.avg_utilisation}%` : "—"}</b>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 900 }}>
                      <thead>
                        <tr><th>Vehicle</th><th>Source</th><th>Driver</th><th>Sites</th><th className="text-end">Carried</th><th className="text-end">Capacity</th><th className="text-end">Utilisation</th></tr>
                      </thead>
                      <tbody>
                        {!(allot.rows || []).length && <tr><td colSpan="7" className="text-center text-muted py-3">No allotments for {allotDate}.</td></tr>}
                        {(allot.rows || []).map(r => (
                          <tr key={r.id}>
                            <td className="fw-500">{r.plate}</td>
                            <td>{r.source || "—"}</td>
                            <td>{r.driver_name || "—"}</td>
                            <td>{(r.sites || []).map((x, j) => <span key={j} className="me-2 text-nowrap">{x.site_name} <b>({x.qty})</b></span>)}</td>
                            <td className="text-end">{num(r.carried)}</td>
                            <td className="text-end">{num(r.capacity)}</td>
                            <td className="text-end fw-600" style={{ color: pctColor(r.carried_pct) }}>{r.carried_pct != null ? `${r.carried_pct}%` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-muted small mb-2">
                    {fuel.summary?.vehicles || 0} vehicles · {num(fuel.summary?.km)} km · {num(fuel.summary?.qty)} L · AED {money(fuel.summary?.amount)} ·
                    avg mileage <b>{fuel.summary?.mileage != null ? `${fuel.summary.mileage} km/L` : "—"}</b>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 820 }}>
                      <thead>
                        <tr><th>Vehicle</th><th>Type</th><th className="text-end">KM</th><th className="text-end">Fuel Qty (L)</th><th className="text-end">Amount (AED)</th><th className="text-end">Mileage</th><th className="text-end">Cost / KM</th></tr>
                      </thead>
                      <tbody>
                        {!(fuel.rows || []).length && <tr><td colSpan="7" className="text-center text-muted py-3">No fuel entries for {fuelMonth}.</td></tr>}
                        {(fuel.rows || []).map(r => (
                          <tr key={r.vehicle_id}>
                            <td className="fw-500">{r.plate}</td>
                            <td>{r.vehicle_type}</td>
                            <td className="text-end">{num(r.km)}</td>
                            <td className="text-end">{num(r.qty)}</td>
                            <td className="text-end">{money(r.amount)}</td>
                            <td className="text-end fw-600" style={{ color: r.mileage >= 5 ? "#16a34a" : "#d97706" }}>{r.mileage != null ? r.mileage : "—"}</td>
                            <td className="text-end">{r.cost_per_km != null ? money(r.cost_per_km) : "—"}</td>
                          </tr>
                        ))}
                        {!!(fuel.rows || []).length && (
                          <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                            <td className="fw-600" colSpan="2">Total</td>
                            <td className="text-end fw-600">{num(fuel.summary?.km)}</td>
                            <td className="text-end fw-600">{num(fuel.summary?.qty)}</td>
                            <td className="text-end fw-600">{money(fuel.summary?.amount)}</td>
                            <td className="text-end fw-600">{fuel.summary?.mileage}</td>
                            <td className="text-end fw-600">{money(fuel.summary?.cost_per_km)}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </CardBody></Card>
          </>
        )}
      </div>
    </div>
  )
}

export default FleetDashboard
